import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ButtonStandard, Spinner, Text } from "@performyard/py-components";

import { NavChromeFixedContainer } from "../../../../components/NavChromeFixedContainer";
import { ChooseCycleType } from "./ChooseCycleType";
import type { CycleType } from "./Config";
import { ReviewCycleWizard } from "./ReviewCycleWizard";

import { useAvailableCycleTypes } from "./hooks/useAvailableCycleTypes";
import { useCopyInitialData } from "./hooks/useCopyInitialData";

import { deleteSelectionObject } from "../../../../api/services/defs/selection-object";

const REVIEW_CYCLES_PATH = "/company/reviews/cycles";

export const ReviewCycleCreateRouter = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const copyCycleId = searchParams.get("copyCycleId");
	const [selectedType, setSelectedType] = useState<CycleType | null>("manual");
	const [started, setStarted] = useState(false);
	const { options, isLoading, isGroupAdminOnly } = useAvailableCycleTypes();
	const {
		cycleType: copyCycleType,
		initialData: copyInitialData,
		userSelectionId: copyUserSelectionId,
		isLoading: copyLoading,
		isError: copyError,
	} = useCopyInitialData(copyCycleId);

	const shouldSkipSelection =
		!isLoading &&
		((isGroupAdminOnly && options.length === 1 && options[0]?.type === "group") ||
			(options.length === 1 && options[0]?.type === "manual"));
	const resolvedType = shouldSkipSelection ? (options[0]?.type ?? null) : selectedType;

	const backToCycle = (message: string) => (
		<div className="flex h-full flex-col items-center justify-center gap-4">
			<Text size="paragraph-3" color="neutral-9" dangerouslySetClassName="text-center">
				{message}
			</Text>
			<ButtonStandard variant="solid" color="primary" onClick={() => navigate(`/review-cycle/${copyCycleId}`)}>
				Back to review cycle
			</ButtonStandard>
		</div>
	);

	const renderContent = () => {
		if (copyCycleId) {
			if (copyError) {
				return backToCycle("We couldn't load the review cycle to copy. Please try again.");
			}
			if (copyLoading || isLoading || !copyInitialData) {
				return (
					<div className="flex h-full items-center justify-center">
						<Spinner size={32} />
					</div>
				);
			}
			// Copy enters the wizard directly, so enforce the same type gating the normal flow gets from
			// ChooseCycleType — a user can't create a cycle type that isn't available to them.
			if (copyCycleType && !options.some((o) => o.type === copyCycleType && !o.disabled)) {
				return backToCycle("This type of review cycle isn't available to create.");
			}
			return (
				<ReviewCycleWizard
					cycleType={copyCycleType ?? "manual"}
					initialData={copyInitialData}
					onExit={() => {
						// Abandoning the copy: discard the selection we cloned up front so it isn't orphaned.
						if (copyUserSelectionId) deleteSelectionObject(copyUserSelectionId).catch(() => {});
						navigate(`/review-cycle/${copyCycleId}`);
					}}
					onCreated={(cycleId) => navigate(`/review-cycle/${cycleId}`)}
				/>
			);
		}

		if (!started && !shouldSkipSelection) {
			return (
				<ChooseCycleType
					selectedType={selectedType}
					onSelect={setSelectedType}
					onCancel={() => navigate(REVIEW_CYCLES_PATH)}
					onGetStarted={() => setStarted(true)}
				/>
			);
		}

		return (
			<ReviewCycleWizard
				cycleType={resolvedType ?? "manual"}
				onExit={() => navigate(REVIEW_CYCLES_PATH)}
				onCreated={(cycleId) => navigate(`/review-cycle/${cycleId}`)}
			/>
		);
	};

	return <NavChromeFixedContainer>{renderContent()}</NavChromeFixedContainer>;
};
